import $ from "jquery";
import _ from "underscore";
import { memoize } from "lodash";
import RewardSupplierCollection from "src/account-rewards/RewardSupplierCollection";
import TangoModel from "src/account-rewards/gift-cards/RewardSupplierTangoModel";
import CustomRewardModel from "src/account-rewards/custom-rewards/CustomRewardModel";
import ManualCouponModel from "src/account-rewards/coupons/CouponModel";
import SalesforceCouponModel from "src/account-rewards/SFCC/SalesforceCouponModel";
import WebhookCollection from "src/shared/webhooks/WebhookCollection";
import WebhookTypes from "src/shared/webhooks/WebhookTypes";
import RewardSupplierFilterCollection from "src/shared/webhooks/RewardSupplierFilterCollection";
import ComponentModel from "src/shared/components/ComponentModel";
import promiseService from "src/shared/promise-service";

const RewardSupplierModels = [
    TangoModel,
    CustomRewardModel,
    ManualCouponModel,
    SalesforceCouponModel,
];

const data = {
    rewardSuppliers: new RewardSupplierCollection(),
};

function getRewardSuppliers() {
    return data.rewardSuppliers;
}

function loadRewardSupplier(rewardSupplierId, rewardType) {
    const deferred = $.Deferred();
    const Model = _.chain(RewardSupplierModels)
        .filter((Model) => rewardType === Model.type)
        .first()
        .value();
    if (Model) {
        const instance = new Model({
            id: rewardSupplierId,
        });
        $.when(instance.fetch())
            .done(() => {
                deferred.resolve(instance);
                instance.on(
                    "sync",
                    data.rewardSuppliers.fetch.bind(data.rewardSuppliers)
                );
            })
            .fail(deferred.reject);
    } else {
        deferred.reject();
    }
    return deferred;
}

const loadWebhookData = memoize(() => {
    const deferred = $.Deferred();
    const rewardWebhookCollection = WebhookCollection.create(
        WebhookTypes.REWARD
    );
    rewardWebhookCollection.fetch().done(() => {
        const promises = rewardWebhookCollection.map((model) => {
            const filterCollection =
                RewardSupplierFilterCollection.create(model);
            return filterCollection.fetch.bind(filterCollection);
        });
        promiseService.promiseAll(promises).done((response) => {
            deferred.resolve(_.flatten(response));
        });
    });
    return deferred;
});

const loadRewardBanks = memoize((rewardSuppliers) => {
    const deferred = $.Deferred();
    const collectibleRewardSuppliers = rewardSuppliers?.filter(
        (rewardSupplier) =>
            rewardSupplier.display_type.toLowerCase().includes("collectible") ||
            rewardSupplier.display_type.toLowerCase().includes("bankable")
    );

    const allComponentIds = _.uniq(
        collectibleRewardSuppliers
            ?.map((rewardSupplier) =>
                rewardSupplier.component_references.map(
                    (component) => component.component_id
                )
            )
            .flat()
            .sort()
    );

    const componentPromises = allComponentIds?.map((componentId) => {
        const model = ComponentModel.create({
            id: componentId,
        });
        return model.fetch.bind(model);
    });

    promiseService.promiseAll(componentPromises || []).done((components) => {
        if (!components) {
            deferred.resolve([]);
            return;
        }

        const rewardSupplierComponentIdsForRewardBanks = _.uniq(
            allComponentIds.filter((componentId, index, array) => {
                const componentRefs =
                    components.find((component) => component.id === componentId)
                        ?.component_references || [];
                const componentRefIds = componentRefs
                    .map((ref) => ref.component_id)
                    .sort()
                    .join(",");
                return (
                    array.findIndex((id) => {
                        const refs =
                            components.find(
                                (refComponent) => refComponent.id === id
                            )?.component_references || [];
                        return (
                            refs
                                .map((ref) => ref.component_id)
                                .sort()
                                .join(",") === componentRefIds
                        );
                    }) === index
                );
            })
        );

        const rewardBanks = components
            .filter((component) =>
                rewardSupplierComponentIdsForRewardBanks.includes(component.id)
            )
            .map((component) => {
                return component.component_references.map(
                    (reference) => reference.component_id
                );
            })
            .flat();

        promiseService
            .promiseAll(
                rewardBanks.map((rewardBankId) => {
                    const model = ComponentModel.create({
                        id: rewardBankId,
                    });
                    return model.fetch.bind(model);
                })
            )
            .done((rewardBanks) => {
                deferred.resolve(rewardBanks);
            });
    });
    return deferred;
});

export default {
    getRewardSuppliers,
    loadRewardSupplier,
    loadWebhookData,
    loadRewardBanks,
};
